/* istanbul ignore file */
import {
  EngagementStatusBlock,
  Integer,
  MpxItemResponse,
  MpxListResponse,
  MpxResponseBase,
  MpxResponseError,
  OrganizationItem,
  ProgramEngagement,
  UserId,
} from '../types'
import {
  Field,
  ProgramConfig,
  ProgramStageType,
  ProgramTask,
  ResourceDataType,
} from '../types/Program'
import {
  GeneralResourceDetail,
  ResourceDetail,
  ResourceDetailsUpdate,
} from '../types/ResourceDetails'
import { generatePath } from '../utils'
import { DetailsResponse } from './types'
import { getMpAgent } from './utils'
import { queueRequest } from './utils/requestQueue'

const agent = getMpAgent()

/**
 * @todo: better typing
 * If you want to use a filter, you must _NOT_ pass in the programId
 */
export type ProgramEngagementsQueryParams = {
  programId?: Array<ProgramConfig['programId']>
  orgId?: OrganizationItem['orgInfo']['orgId']
  currentStepType?: ProgramStageType
  updatedAtGt?: Date
  updatedAtLte?: Date
  hasUncommittedFields?: boolean
  filters?: [
    {
      userId?: UserId | UserId[]
      programId?: string | string[]
    }
  ]
}

export type ProgramEngagementsDetailParams = {
  id: ProgramEngagement['id']
}

export type CreateProgramEngagementParams = Pick<ProgramEngagement, 'userId' | 'programId'> &
  Partial<Pick<ProgramEngagement, 'name' | 'space'>> &
  Partial<{ doesUpsert?: boolean }> & { fields?: string[] }

export type UpdateProgramEngagementParams = Pick<ProgramEngagement, 'id'> &
  Partial<Pick<ProgramEngagement, 'name'>> & {
    action: 'merge' | 'replace' | 'remove'
    fields: Array<Field['resourceId']>
  }

export type ProgramEngagementConfirmTaskParams = Pick<ProgramEngagement, 'id'> & {
  taskId: ProgramTask['id']
}

export type ProgramEngagementUnconfirmTasksParams = Pick<ProgramEngagement, 'id'> & {
  taskIds: ProgramTask['id'][]
}

export type EqipApplicationResultsParams = Pick<ProgramEngagement, 'id'> & {
  year?: number
  file?: any
  fileId?: string
  fileName?: string
  responseReceived?: boolean
  applicationApproved?: boolean
  totalContractValue?: number
  willReapply?: boolean
}

export type ProgramEngagementLockDetailsError = {
  message: string
} & MpxResponseError

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const UPDATE_PROGRAM_ENGAGEMENT_ERROR_TYPE = ['alreadyInWorkflow'] as const
export type UpdateProgramEngagementErrorType = typeof UPDATE_PROGRAM_ENGAGEMENT_ERROR_TYPE[number]

export type UpdateProgramEngagementError = {
  fields: Array<{
    id: Field['resourceId']
    message: string
  }>
  message: string
  errorType: UpdateProgramEngagementErrorType
} & MpxResponseError

export type DataEntryGatedValue = { gate: boolean; data?: string }

export type SingularTaskTraitDetail<T = string | boolean | DataEntryGatedValue> = {
  traitId: string
  value: T | null | any
}

export type MultipleTaskTraitDetail<T = string[] | boolean | DataEntryGatedValue> = {
  traitId: string
  value: T | null | any
  year?: number
}

export type FieldTaskTraitDetail = { traitId: string; year?: number; result?: string }

export type DataResourceDetail = Omit<ResourceDetail, 'traitId'> & { traitId: string }
export type DataResourceDetailRequirement = {
  dataType: ResourceDataType
  traitId: string
  year?: Integer
}

export type ResourceDetailDeleteRequest = {
  resourceId: string
  traitId: string
  year?: number
}

export interface UpdateTaskDataArguments {
  programEngagementId: string
  taskId: string
  updates: {
    fieldDetails?: {
      resourceId: string
      details: FieldTaskTraitDetail[]
    }[]
    userDetails?: SingularTaskTraitDetail[] | MultipleTaskTraitDetail[]
    wfDetails?: SingularTaskTraitDetail[] | MultipleTaskTraitDetail[]
  }
}

export type TaskFieldDetails = { resourceId: string; details: DataResourceDetail[] }

// This is a rendered version of the trait config for dataEntry traits
// The params may, for instance, include dynamic values according to program design + context
export type TaskDataTraitStatus<T = any> = {
  dataType: ResourceDataType
  displayType: ResourceDataType
  parentTraitId: string
  title: string
  traitId: string
  yearly: boolean
  params: T
}

export interface TaskDataResponse extends MpxResponseBase {
  detailRequirements: DataResourceDetailRequirement[]
  status: EngagementStatusBlock

  fieldDetails?: TaskFieldDetails[]
  userDetails?: DataResourceDetail[]
  wfDetails?: DataResourceDetail[]

  traits: TaskDataTraitStatus[]
}

const PATHS = {
  CONFIRM: '/program-engagements/:programEngagementId/confirm',
  DETAIL: '/program-engagements/:programEngagementId',
  FIELDS: '/program-engagements/:programEngagementId/fields',
  TASK_DATA: '/program-engagements/:programEngagementId/task-data/:taskId',
  LIST: '/program-engagements',
  RESOURCE_DETAILS: '/program-engagements/:programEngagementId/details',
  UPLOAD_PAYMENT_FILE: '/program-engagements/:programEngagementId/upload/eqipApplicationResults',
  DOWNLOAD_PAYMENT_FILE: '/program-engagements/:programEngagementId/file/eqipApplicationResults',
}

export class ProgramEngagementsAPI {
  static list(params: ProgramEngagementsQueryParams) {
    return agent.get<MpxListResponse<ProgramEngagement>>(PATHS.LIST, {
      params: {
        ...params,
        programId: params.programId?.join(','),
        filters: !!params.filters?.length
          ? params.filters.map(filter => JSON.stringify(filter)).join(',')
          : undefined,
      },
    })
  }

  static detail({ id }: ProgramEngagementsDetailParams, options?: any) {
    return agent.get<ProgramEngagement>(
      generatePath(PATHS.DETAIL, { programEngagementId: id }),
      options
    )
  }

  static create(params: CreateProgramEngagementParams) {
    return agent.post<ProgramEngagement>(PATHS.LIST, params)
  }

  static update(params: UpdateProgramEngagementParams) {
    const { id, ...rest } = params
    return agent.put<ProgramEngagement>(
      generatePath(PATHS.DETAIL, { programEngagementId: id }),
      rest
    )
  }

  static delete({ id }: ProgramEngagementsDetailParams) {
    return agent.delete<MpxItemResponse<ProgramEngagement>>(
      generatePath(PATHS.DETAIL, { programEngagementId: id })
    )
  }

  static fields({ id }: ProgramEngagementsDetailParams) {
    return agent.get<MpxListResponse<Field>>(
      generatePath(PATHS.FIELDS, { programEngagementId: id })
    )
  }

  static confirmTask({ id, taskId }: ProgramEngagementConfirmTaskParams) {
    return agent.put<ProgramEngagement>(generatePath(PATHS.CONFIRM, { programEngagementId: id }), {
      taskId,
    })
  }

  static unconfirmTasks({ id, taskIds }: ProgramEngagementUnconfirmTasksParams) {
    return agent.delete<ProgramEngagement>(
      generatePath(PATHS.CONFIRM, { programEngagementId: id }),
      {
        params: { taskIds: taskIds.join(',') },
      }
    )
  }

  static getDetails<T extends GeneralResourceDetail = GeneralResourceDetail>({
    resourceId,
  }: {
    resourceId: string
  }) {
    return agent
      .get<DetailsResponse<T>>(
        generatePath(PATHS.RESOURCE_DETAILS, { programEngagementId: resourceId })
      )
      .then(response => response?.data)
  }

  static updateDetails({ resourceId, details }: ResourceDetailsUpdate) {
    return queueRequest<MpxItemResponse<GeneralResourceDetail>>(() =>
      agent
        .patch<MpxItemResponse<GeneralResourceDetail>>(
          generatePath(PATHS.RESOURCE_DETAILS, { programEngagementId: resourceId }),
          {
            details,
          }
        )
        .then(response => response?.data)
    )
  }

  static removeDetail({ resourceId, traitId, year }: ResourceDetailDeleteRequest) {
    return agent.patch(generatePath(PATHS.RESOURCE_DETAILS, { programEngagementId: resourceId }), {
      details: [{ traitId, year }],
    })
  }

  static getTaskData({
    programEngagementId,
    taskId,
  }: {
    programEngagementId: string
    taskId: string
  }) {
    return agent
      .get<TaskDataResponse>(generatePath(PATHS.TASK_DATA, { programEngagementId, taskId }))
      .then(response => response?.data)
  }

  static updateTaskData({ programEngagementId, taskId, updates }: UpdateTaskDataArguments) {
    return agent
      .patch<MpxItemResponse<GeneralResourceDetail>>(
        generatePath(PATHS.TASK_DATA, {
          programEngagementId,
          taskId,
        }),
        updates
      )
      .then(response => response?.data)
  }

  static uploadApplicationResults(params: EqipApplicationResultsParams) {
    const { id, ...rest } = params
    if (rest.file) {
      const formData = new FormData()
      rest.file && formData.append('file', rest.file)
      rest.fileId && formData.append('fileId', rest.fileId)
      rest.fileName && formData.append('fileName', rest.fileName)
      rest.year && formData.append('year', rest.year.toString())
      rest.responseReceived && formData.append('responseReceived', rest.responseReceived.toString())
      rest.applicationApproved &&
        formData.append('applicationApproved', rest.applicationApproved.toString())
      rest.totalContractValue &&
        formData.append('totalContractValue', rest.totalContractValue.toString())
      return agent.patch<any>(
        generatePath(PATHS.UPLOAD_PAYMENT_FILE, { programEngagementId: id }),
        formData
      )
    } else {
      return agent.patch<any>(
        generatePath(PATHS.UPLOAD_PAYMENT_FILE, { programEngagementId: id }),
        params
      )
    }
  }

  static getApplicationResultsUrl(params: { programEngagementId: string; year: number }) {
    const url = `${generatePath(PATHS.DOWNLOAD_PAYMENT_FILE, {
      programEngagementId: params.programEngagementId,
    })}?year=${params.year}`

    return url
  }
}
